import { Link } from "react-router-dom";
import { BookOpen, CheckCircle2 } from "lucide-react";

// Study-mode results: only what was missed (clue → answer → explanation). No points.
const dedupe = (items) => {
  const seen = new Map();
  items.forEach((m) => {
    const hit = seen.get(m.id);
    if (hit) hit.count += 1;
    else seen.set(m.id, { ...m, count: 1 });
  });
  return [...seen.values()];
};

export function StudySummary({ kicker, missed: rawMissed, attempted, onRestart, restartLabel = "Study again", testid = "study-summary" }) {
  const missed = dedupe(rawMissed);
  return (
    <div className="panel p-10" data-testid={testid}>
      <div className="text-[11px] tracking-[0.3em] text-muted-foreground mb-3">// {kicker} · STUDY MODE</div>
      {missed.length === 0 ? (
        <div className="flex items-center gap-4 py-6" data-testid="study-clean-sheet">
          <CheckCircle2 className="w-10 h-10 text-phosphor shrink-0" />
          <div>
            <h2 className="text-2xl font-extrabold">Clean sheet.</h2>
            <p className="text-sm text-muted-foreground mt-1">Nothing missed across {attempted} item{attempted === 1 ? "" : "s"}. Try a harder set or switch to Competition.</p>
          </div>
        </div>
      ) : (
        <>
          <h2 className="text-2xl font-extrabold mb-1">
            <span className="text-destructive">{missed.length}</span> to review
          </h2>
          <p className="text-sm text-muted-foreground mb-6">Out of {attempted} attempted. Points are hidden in Study mode — these are the only things that matter right now.</p>
          <div className="space-y-3" data-testid="study-missed-list">
            {missed.map((m, i) => (
              <div key={m.id + i} className="border border-border p-4 grid gap-1" data-testid={`study-missed-${m.id}`}>
                <div className="text-xs text-muted-foreground">{m.prompt}</div>
                <div className="text-phosphor font-semibold">
                  {m.answer}
                  {m.count > 1 && <span className="ml-2 text-[10px] tracking-widest text-destructive border border-destructive/50 px-1.5 py-0.5" data-testid={`study-missed-count-${m.id}`}>×{m.count}</span>}
                </div>
                {m.detail && <div className="text-xs text-muted-foreground mt-1 leading-relaxed">{m.detail}</div>}
              </div>
            ))}
          </div>
        </>
      )}
      <div className="mt-8 flex flex-wrap gap-3">
        {missed.length > 0 && (
          <Link to="/flashcards" data-testid="study-to-flashcards" className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 text-xs tracking-widest uppercase font-semibold hover:bg-primary/90">
            <BookOpen className="w-4 h-4" /> Study these as flashcards
          </Link>
        )}
        {onRestart && (
          <button onClick={onRestart} data-testid="study-restart-btn" className="border border-phosphor/50 px-6 py-3 text-xs tracking-widest uppercase hover:bg-primary/10">
            {restartLabel}
          </button>
        )}
        <Link to="/dashboard" className="border border-border px-6 py-3 text-xs tracking-widest uppercase hover:bg-primary/10" data-testid="study-to-dashboard">
          Dashboard
        </Link>
      </div>
    </div>
  );
}
